import React, { useMemo, useState } from 'react';

import { LoadingButton } from '@mui/lab';
import { Box, Tab, Tabs } from '@mui/material';

import { Resource } from '../../../../../common/constants/resource';
import { AutoMedicalClaimsClaimsTable } from './AutoMedicalClaimsClaimsTable';
import { AutoMedicalClaimsResultRow } from './AutoMedicalClaimsResultRow';
import { AutoMedicalClaimsRuleHitsTable } from './AutoMedicalClaimsRuleHitsTable';
import { MAX_VISIBLE_RULES, sortRuleHits } from './ruleHitsHelpers';
import { getAllClaims } from './ruleHitsHelpers';
import { MedicalClaim, RuleHit } from './types';

type AutoMedicalClaimsRuleHitsPanelProps = {
  ruleHits: RuleHit[];
  claimsById: Record<number, MedicalClaim>;
  independentClaims: MedicalClaim[];
  hasHit: boolean;
  hasClear: boolean;
  loadingMagicLink: boolean;
  onOpenMillimanPortal: () => Promise<void>;
};

type PanelTab = 'ruleHits' | 'independentClaims' | 'allClaims';

export const AutoMedicalClaimsRuleHitsPanel = ({
  ruleHits,
  claimsById,
  independentClaims,
  hasHit,
  hasClear,
  loadingMagicLink,
  onOpenMillimanPortal,
}: AutoMedicalClaimsRuleHitsPanelProps) => {
  const [expandedRuleIds, setExpandedRuleIds] = useState<string[]>([]);
  const [showAllRules, setShowAllRules] = useState(false);
  const [activeTab, setActiveTab] = useState<PanelTab>('ruleHits');
  const allClaims = useMemo(
    () => getAllClaims(claimsById, independentClaims),
    [claimsById, independentClaims],
  );

  const onToggleRuleClaims = (ruleId: string) => {
    setExpandedRuleIds(previousRuleIds =>
      previousRuleIds.includes(ruleId)
        ? previousRuleIds.filter(id => id !== ruleId)
        : [...previousRuleIds, ruleId],
    );
  };

  const onToggleViewMore = () => {
    setShowAllRules(previousValue => {
      const nextValue = !previousValue;
      if (!nextValue) {
        const visibleRuleIds = sortRuleHits(ruleHits, claimsById)
          .slice(0, MAX_VISIBLE_RULES)
          .map(rule => rule.id);
        setExpandedRuleIds(previousRuleIds =>
          previousRuleIds.filter(ruleId => visibleRuleIds.includes(ruleId)),
        );
      }

      return nextValue;
    });
  };

  return (
    <Box className="auto-medical-claims-panel-wrapper">
      {(hasHit || hasClear) && (
        <Box className="auto-medical-claims-panel">
          <AutoMedicalClaimsResultRow hasHit={hasHit} hasClear={hasClear} />

          <Tabs
            value={activeTab}
            onChange={(_event, value: PanelTab) => setActiveTab(value)}
            className="auto-medical-claims-tabs"
            variant="standard"
          >
            <Tab
              value="ruleHits"
              label={`Rule Hits (${ruleHits.length})`}
              className="auto-medical-claims-tab"
            />
            <Tab
              value="independentClaims"
              label={`Independent Claims (${independentClaims.length})`}
              className="auto-medical-claims-tab"
            />
            <Tab
              value="allClaims"
              label={`All Claims (${allClaims.length})`}
              className="auto-medical-claims-tab"
            />
          </Tabs>

          {activeTab === 'ruleHits' && (
            <AutoMedicalClaimsRuleHitsTable
              ruleHits={ruleHits}
              claimsById={claimsById}
              expandedRuleIds={expandedRuleIds}
              showAllRules={showAllRules}
              onToggleRuleClaims={onToggleRuleClaims}
              onToggleViewMore={onToggleViewMore}
            />
          )}

          {activeTab === 'independentClaims' &&
            (independentClaims.length > 0 ? (
              <Box className="auto-medical-claims-independent-claims">
                <AutoMedicalClaimsClaimsTable claims={independentClaims} />
              </Box>
            ) : (
              <Box className="auto-medical-claims-empty">
                No independent claims
              </Box>
            ))}

          {activeTab === 'allClaims' &&
            (allClaims.length > 0 ? (
              <Box className="auto-medical-claims-all-claims">
                <AutoMedicalClaimsClaimsTable claims={allClaims} />
              </Box>
            ) : (
              <Box className="auto-medical-claims-empty">No claims</Box>
            ))}

          <Box className="auto-medical-claims-footer">
            <LoadingButton
              className="auto-medical-claims-footer-button"
              loading={loadingMagicLink}
              size="small"
              variant="text"
              onClick={onOpenMillimanPortal}
            >
              <span className="auto-medical-claims-footer-link">
                View Medical Claim (Milliman portal)
              </span>
            </LoadingButton>
            <span className="auto-medical-claims-footer-meta">
              {Resource.NA}
            </span>
          </Box>
        </Box>
      )}
    </Box>
  );
};
